import { useState, Fragment, useEffect, createContext, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  Collapse,
  Table,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ThemeProvider } from '@mui/material';

import ServiceLinePaymentsTable from './ServiceLinePaymentsTable';
import { multiplePaymentsTableTheme } from '../../../../../../utils/theme';
import { FORTA_BLUE } from '../../../../../../utils/colors';
import { claimsActions } from '../../../../../../store';
import { DATE_DISPLAY_FORMAT } from '../../../../../../constants';
import { usDollarFormatter } from '../../../../../../helpers';
import { FHConfirmDialog } from '../../../../../base';
import { useClaimPaymentEditable } from '../../../../../../hooks';
import { ClaimStatus } from '../../constants';

import type { SetStateAction, Dispatch } from 'react';
import type { IClaim, IClaimPolicy, IServiceLine } from '../../../../../../utils/types';

interface IServiceLineRowProps {
  isEditable: boolean;
  claimId: number;
  policies: IClaimPolicy[];
  serviceLine: IServiceLine;
  showModifierLocationColumns: boolean;
}

interface IPaymentsTableFooterProps {
  claim: IClaim;
  showModifierLocationColumns: boolean;
}

type ItemToModified = {
  serviceLineId?: number;
  paymentId?: number;
  data?: any;
  action: 'save' | 'delete' | 'edit';
} | null;

export const PayorResponsibilityContext = createContext<
  | {
      expandedServiceLines: number[];
      itemBeingModified: ItemToModified;
      setInvalidDateError: (isValid: boolean) => void;
      setRowToBeExpanded: Dispatch<SetStateAction<number | null>>;
      setRowToBeCollapsed: Dispatch<SetStateAction<number | null>>;
      setExpandedServiceLines: Dispatch<SetStateAction<number[]>>;
      setItemBeingModified: Dispatch<SetStateAction<ItemToModified>>;
      handleAddNewPayment: (serviceLineId: number) => void;
      handleDeletePayment: (serviceLineId: number, paymentId?: number) => void;
      handleEditRow: (serviceLineId: number, paymentId?: number) => void;
      handleSavePayment: (serviceLineId?: number, data?: any, paymentId?: number) => void;
    }
  | undefined
>(undefined);

export const usePayorResponsibilityContext = () => {
  const context = useContext(PayorResponsibilityContext);
  if (!context) {
    throw new Error(
      'usePayorResponsibilityContext must be used within a PayorResponsibilityContext.Provider',
    );
  }
  return context;
};

function ServiceLineRow({
  isEditable,
  claimId,
  serviceLine,
  policies,
  showModifierLocationColumns,
}: IServiceLineRowProps) {
  const {
    units,
    billed,
    deductibles,
    co_insurance,
    co_pay,
    amount,
    allowable,
    adjustments,
    service_date,
    payments,
    location,
    modifier,
  } = serviceLine;
  const {
    itemBeingModified,
    expandedServiceLines,
    handleSavePayment,
    handleAddNewPayment,
    setRowToBeCollapsed,
    setRowToBeExpanded,
    setExpandedServiceLines,
  } = usePayorResponsibilityContext();

  const open = expandedServiceLines.includes(serviceLine.id);

  const isAllowableStored = serviceLine.payments.some((payment) => payment.allowable !== null);

  const noPayments = payments.length === 0;

  if (noPayments && open) {
    handleAddNewPayment(serviceLine.id);
  }

  const handleRowExpand = () => {
    // save unsaved row item
    if (itemBeingModified && !open && noPayments) {
      handleSavePayment();
      setRowToBeExpanded(serviceLine.id);
      return;
    }

    if (open && itemBeingModified?.serviceLineId === serviceLine.id) {
      handleSavePayment();
      setRowToBeCollapsed(serviceLine.id);
      return;
    }

    if (noPayments) {
      handleAddNewPayment(serviceLine.id);
    }

    if (open) {
      setExpandedServiceLines((prev) => prev.filter((id) => id !== serviceLine.id));
    } else {
      setExpandedServiceLines((prev) => [...prev, serviceLine.id]);
    }
  };

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            data-testid="service-line-payment-expand"
            onClick={handleRowExpand}
          >
            {open ? (
              <KeyboardArrowDownIcon sx={{ color: FORTA_BLUE.primary }} />
            ) : (
              <KeyboardArrowRightIcon
                sx={{ color: FORTA_BLUE.primary, opacity: noPayments ? 0.3 : 1 }}
              />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{moment(service_date).format(DATE_DISPLAY_FORMAT)}</TableCell>
        {showModifierLocationColumns && (
          <>
            <TableCell align="center">{location ?? '--'}</TableCell>
            <TableCell align="center">{modifier?.join(', ') ?? '--'}</TableCell>
          </>
        )}
        <TableCell align="right">{units}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(billed)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(allowable)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(adjustments)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(deductibles)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(co_insurance)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(co_pay)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(amount)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0 }} colSpan={12}>
          <Collapse
            in={open}
            unmountOnExit
            timeout="auto"
            sx={{ backgroundColor: FORTA_BLUE.light3 }}
          >
            <ServiceLinePaymentsTable
              isEditable={isEditable}
              claimId={claimId}
              policies={policies}
              payments={payments}
              serviceLineId={serviceLine.id}
              isAllowableStored={isAllowableStored}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

function PayorResponsibilityTableFooter({
  claim,
  showModifierLocationColumns,
}: IPaymentsTableFooterProps) {
  return (
    <TableFooter sx={{ color: `${FORTA_BLUE.primary} !important`, fontWeight: 700 }}>
      <TableRow>
        <TableCell sx={{ borderBottomLeftRadius: '8px' }}></TableCell>
        <TableCell>Totals</TableCell>
        {showModifierLocationColumns && (
          <>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </>
        )}
        <TableCell align="right">{claim?.units ?? 0}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(claim?.billed ?? 0)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(claim?.allowable ?? 0)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(claim?.adjustments ?? 0)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(claim?.deductible ?? 0)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(claim?.co_insurance ?? 0)}</TableCell>
        <TableCell align="right">{usDollarFormatter.format(claim?.co_pay ?? 0)}</TableCell>
        <TableCell sx={{ borderBottomRightRadius: '8px' }} align="right">
          {usDollarFormatter.format(claim?.amount_insurance_paid ?? 0)}
        </TableCell>
      </TableRow>
    </TableFooter>
  );
}

interface IPayorResponsibilityTableProps {
  claim: IClaim;
}

export default function PayorResponsibilityTable({ claim }: IPayorResponsibilityTableProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const isEditable = useClaimPaymentEditable(claim) && claim?.status !== ClaimStatus.UNBILLABLE;

  const showModifierLocationColumns = useMemo(() => {
    if (!claim) return false;
    return claim.service_lines?.some((serviceLine) =>
      Boolean(serviceLine.location || serviceLine.modifier),
    );
  }, [claim]);

  const [serviceLines, setServiceLines] = useState<IServiceLine[]>([]);
  const [expandedServiceLines, setExpandedServiceLines] = useState<number[]>([]);
  const [rowToBeExpanded, setRowToBeExpanded] = useState<number | null>(null);
  const [rowToBeCollapsed, setRowToBeCollapsed] = useState<number | null>(null);
  const [invalidDateError, setInvalidDateError] = useState<boolean>(false);
  const [itemBeingModified, setItemBeingModified] = useState<{
    serviceLineId?: number;
    paymentId?: number;
    data?: any;
    action: 'save' | 'edit' | 'delete';
  } | null>(null);

  const [showSaveDisclaimer, setShowSaveDisclaimer] = useState<boolean>(false);
  const [showDeleteDisclaimer, setShowDeleteDisclaimer] = useState<boolean>(false);

  useEffect(() => {
    setServiceLines(JSON.parse(JSON.stringify(claim?.service_lines ?? [])));
  }, [claim]);

  const claimId = claim.id;
  if (claimId === undefined) return null;

  const handleEditRow = (serviceLineId: number, paymentId?: number) => {
    setItemBeingModified({ serviceLineId, paymentId, action: 'edit' });
  };

  const handleAddNewPayment = (serviceLineId: number) => {
    if (!isEditable) return;
    const serviceLine = serviceLines.find((sl) => sl.id === serviceLineId);
    if (!serviceLine) return;

    serviceLine.payments.push({
      policy_id: null,
      deductibles: null,
      co_insurance: null,
      co_pay: null,
      amount: null,
      allowable: null,
      adjustments: null,
      date_received: null,
      date_cleared: null,
      mode: null,
      is_amount_paid_to_subscriber: false,
      transaction_id: null,
    });
    setServiceLines([...serviceLines]);
    setItemBeingModified({ serviceLineId, action: 'edit' });
  };

  const handleDeleteUnsavedPayment = (serviceLineId?: number) => {
    if (!serviceLineId) return;
    const serviceLine = serviceLines.find((sl) => sl.id === serviceLineId);
    if (!serviceLine) return;

    serviceLine.payments = serviceLine.payments.filter((payment) => Boolean(payment.id));
    setServiceLines([...serviceLines]);
    setShowDeleteDisclaimer(false);
    if (itemBeingModified?.serviceLineId === serviceLineId && !itemBeingModified?.paymentId) {
      setItemBeingModified(null);
    }
    if (serviceLine.payments.length === 0) {
      setExpandedServiceLines((prev) => prev.filter((id) => id !== serviceLineId));
    }
  };

  const handleDeletePayment = (serviceLineId: number, paymentId?: number) => {
    if (!claim) return;
    setShowDeleteDisclaimer(true);
    setItemBeingModified({ serviceLineId, paymentId, action: 'delete' });
  };

  const handleSavePayment = (serviceLineId?: number, data?: any, paymentId?: number) => {
    if (invalidDateError) {
      toast.error('Invalid date in payment row');
      return;
    }
    if (!claim) return;
    setShowSaveDisclaimer(true);
    setItemBeingModified({
      serviceLineId: serviceLineId ? serviceLineId : itemBeingModified?.serviceLineId,
      data: data ? data : itemBeingModified?.data,
      paymentId: paymentId ? paymentId : itemBeingModified?.paymentId,
      action: 'save',
    });
  };

  const saveLineItem = async () => {
    if (!itemBeingModified || !claim) return;
    const { serviceLineId, paymentId, data } = itemBeingModified;
    if (!serviceLineId || !data) return;

    let response;
    if (paymentId) {
      response = await dispatch(
        claimsActions.updateServiceLinePayment({
          data,
          getAccessTokenSilently,
          claimId,
          serviceLineId,
          paymentId: paymentId,
        }),
      );
    } else {
      response = await dispatch(
        claimsActions.createServiceLinePayment({
          data,
          getAccessTokenSilently,
          claimId,
          serviceLineId,
        }),
      );
    }
    if (!response.error) {
      setItemBeingModified(null);
      setShowSaveDisclaimer(false);
      if (rowToBeExpanded !== null) {
        setExpandedServiceLines((prev) => [...prev, rowToBeExpanded]);
        setRowToBeExpanded(null);
      }
      if (rowToBeCollapsed !== null) {
        setExpandedServiceLines((prev) => prev.filter((id) => id !== rowToBeCollapsed));
        setRowToBeCollapsed(null);
      }
    } else {
      setShowSaveDisclaimer(false);
    }
  };

  const deleteLineItem = async () => {
    if (!itemBeingModified || !claim) return;
    const { serviceLineId, paymentId } = itemBeingModified;

    if (!paymentId) {
      handleDeleteUnsavedPayment(serviceLineId);
      return;
    }
    const response = await dispatch(
      claimsActions.deleteServiceLinePayment({
        getAccessTokenSilently,
        claimId,
        serviceLineId,
        paymentId: paymentId,
      }),
    );
    if (!response.error) {
      setItemBeingModified(null);
      setShowDeleteDisclaimer(false);

      // If the delete payment was the last payment of service line, collapse the service line
      const serviceLine = serviceLines.find((sl) => sl.id === serviceLineId);
      if (
        serviceLine &&
        serviceLine.payments.length === 1 &&
        serviceLine.payments[0].id === paymentId
      ) {
        setExpandedServiceLines((prev) => prev.filter((id) => id !== serviceLineId));
      }
    }
  };

  return claim ? (
    <>
      <PayorResponsibilityContext.Provider
        value={{
          expandedServiceLines,
          itemBeingModified,
          setInvalidDateError,
          setRowToBeExpanded,
          setRowToBeCollapsed,
          setExpandedServiceLines,
          setItemBeingModified,
          handleAddNewPayment,
          handleDeletePayment,
          handleSavePayment,
          handleEditRow,
        }}
      >
        <ThemeProvider theme={multiplePaymentsTableTheme}>
          <TableContainer sx={{ py: 3 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Date</TableCell>
                  {showModifierLocationColumns && (
                    <>
                      <TableCell align="center">Location</TableCell>
                      <TableCell align="center">Modifier</TableCell>
                    </>
                  )}
                  <TableCell align="right">Units</TableCell>
                  <TableCell align="right">Billed</TableCell>
                  <TableCell align="right">Allowable</TableCell>
                  <TableCell align="right">Adjust.</TableCell>
                  <TableCell align="right">Deduct.</TableCell>
                  <TableCell align="right">Co-ins.</TableCell>
                  <TableCell align="right">Co-pay</TableCell>
                  <TableCell align="right">Payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceLines.map((serviceLine) => (
                  <ServiceLineRow
                    policies={claim.policies}
                    isEditable={isEditable}
                    claimId={claimId}
                    serviceLine={serviceLine}
                    key={serviceLine.id}
                    showModifierLocationColumns={showModifierLocationColumns}
                  />
                ))}
              </TableBody>
              <PayorResponsibilityTableFooter
                claim={claim}
                showModifierLocationColumns={showModifierLocationColumns}
              />
            </Table>
          </TableContainer>
        </ThemeProvider>
      </PayorResponsibilityContext.Provider>
      <FHConfirmDialog
        open={showDeleteDisclaimer}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        content={'Are you sure you want to delete this line item?'}
        submittext="Delete"
        submitBtnTestId="confirmDeleteBtn"
        canceltext="Cancel"
        cancelBtnTestId="cancel"
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setShowDeleteDisclaimer(false);
          setItemBeingModified(null);
        }}
        onSubmit={deleteLineItem}
      />

      <FHConfirmDialog
        open={showSaveDisclaimer}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        content={'Please ensure all information is correct before saving.'}
        submittext="Save"
        submitBtnTestId="save"
        canceltext="Discard changes"
        cancelBtnTestId="cancel"
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setShowSaveDisclaimer(false);
          if (itemBeingModified) {
            handleDeleteUnsavedPayment(itemBeingModified.serviceLineId);
            setItemBeingModified(null);
          }
        }}
        onSubmit={saveLineItem}
      />
    </>
  ) : null;
}
